<template>
  <AppModal
    :open="open"
    :title="title || $t('app.cropTitle')"
    :description="description || $t('app.cropDescription')"
    test-id="image-crop-modal"
    :close-on-backdrop-click="false"
    :stack-on-top="stackOnTop"
    @close="onClose"
  >
    <div class="space-y-4">
      <p v-if="hint" class="text-sm text-slate-600 dark:text-slate-400">{{ hint }}</p>
      <div v-if="imageUrl" class="rounded-xl overflow-hidden bg-slate-100 dark:bg-zinc-800 min-h-[280px] max-h-[70vh] flex flex-col">
        <div
          v-if="squarePadLoading"
          class="flex-1 min-h-[240px] flex items-center justify-center text-sm text-slate-600 dark:text-slate-400"
          role="status"
        >
          {{ $t('app.loading') }}
        </div>
        <Cropper
          v-else-if="cropperSrc"
          ref="cropperRef"
          class="cropper flex-1 min-h-[240px]"
          :src="cropperSrc"
          :stencil-props="stencilProps"
          default-boundaries="fill"
          v-bind="cropperBehavior"
          @ready="onCropperReady"
        />
      </div>
    </div>
    <template #footer>
      <AppButton variant="secondary" class="min-h-[44px]" data-testid="image-crop-cancel" @click="onClose">
        {{ $t('app.cancel') }}
      </AppButton>
      <AppButton variant="primary" class="min-h-[44px]" data-testid="image-crop-confirm" :disabled="confirming" @click="onConfirm">
        <template v-if="confirming" #icon>
          <span class="material-icons animate-spin">sync</span>
        </template>
        {{ confirming ? $t('app.saving') : $t('app.cropApply') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'
import { prepareSquareLogoCropSource } from '@/utils/imageUpload'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** Data URL or object URL of the image to crop */
  imageUrl: { type: String, default: '' },
  /** Fixed aspect ratio (e.g. 1 for square, 21/9 for banner). Omit for free crop. */
  aspectRatio: { type: Number, default: undefined },
  /**
   * Letterbox fill when padding non-square sources for square crop.
   * Omit for transparent padding (required for black text on transparent PNG logos).
   */
  squarePadFillColor: { type: String, default: undefined },
  /** `png` keeps alpha through crop (logos); `jpeg` smaller files (banners, photos). */
  exportFormat: { type: String, default: 'jpeg', validator: (v) => v === 'jpeg' || v === 'png' },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  hint: { type: String, default: '' },
  /** Original file name for the output File */
  fileName: { type: String, default: 'image.jpg' },
  /** Raise above high z-index shells (e.g. public quick-add bottom sheet). */
  stackOnTop: { type: Boolean, default: false },
})

const emit = defineEmits(['confirm', 'cancel'])

const cropperRef = ref(null)
const confirming = ref(false)

/** Actual image passed to Cropper (may be letterboxed square for wide/tall logos). */
const cropperSrc = ref('')
const squarePadLoading = ref(false)
let paddedRevoke = () => {}
let prepareToken = 0

function isSquareLogoAspect() {
  const ar = props.aspectRatio
  return ar != null && ar > 0 && Math.abs(ar - 1) < 0.0001
}

async function prepareCropperSource() {
  const token = ++prepareToken
  paddedRevoke()
  paddedRevoke = () => {}

  const raw = props.imageUrl
  if (!raw) {
    cropperSrc.value = ''
    squarePadLoading.value = false
    return
  }

  if (!isSquareLogoAspect()) {
    cropperSrc.value = raw
    squarePadLoading.value = false
    return
  }

  squarePadLoading.value = true
  try {
    const padOpts =
      props.squarePadFillColor != null && String(props.squarePadFillColor).trim() !== ''
        ? { fillColor: props.squarePadFillColor }
        : {}
    const { url, revokeIfNeeded } = await prepareSquareLogoCropSource(raw, padOpts)
    if (token !== prepareToken) {
      revokeIfNeeded()
      return
    }
    paddedRevoke = revokeIfNeeded
    cropperSrc.value = url
  } catch {
    if (token !== prepareToken) return
    cropperSrc.value = raw
  } finally {
    if (token === prepareToken) squarePadLoading.value = false
  }
}

watch(
  () => [props.imageUrl, props.aspectRatio, props.squarePadFillColor],
  () => {
    void prepareCropperSource()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  paddedRevoke()
  paddedRevoke = () => {}
})

/**
 * Square logo/menu: stencil fills the modal (not a tiny box in image space). User zooms/pans the image under it.
 * Wide sources are letterboxed to a square bitmap first so the full logo fits.
 */
function stencilSizeSquareViewport({ boundaries }) {
  const bw = boundaries.width
  const bh = boundaries.height
  const m = Math.min(bw, bh)
  const inset = Math.min(20, Math.max(8, m * 0.04))
  const s = Math.max(160, m - inset * 2)
  return { width: s, height: s }
}

/**
 * Banner: stencil fits inside the preview with 21:9 aspect.
 */
function stencilSizeBannerViewport({ boundaries }) {
  const bw = boundaries.width
  const bh = boundaries.height
  const ar = 21 / 9
  let w = bw
  let h = w / ar
  if (h > bh) {
    h = bh
    w = h * ar
  }
  const inset = Math.min(16, bw * 0.02)
  w = Math.max(200, w - inset * 2)
  h = w / ar
  if (h > bh - inset * 2) {
    h = Math.max(80, bh - inset * 2)
    w = h * ar
  }
  return { width: w, height: h }
}

const stencilProps = computed(() => {
  const ar = props.aspectRatio
  if (ar == null || ar <= 0) {
    return {}
  }
  const base = { aspectRatio: ar }
  // Fixed frame in the UI; move/zoom the image (matches mobile crop UX).
  if (isSquareLogoAspect()) {
    return {
      ...base,
      handlers: {},
      movable: false,
      resizable: false,
    }
  }
  if (Math.abs(ar - 21 / 9) < 0.02) {
    return {
      ...base,
      handlers: {},
      movable: false,
      resizable: false,
    }
  }
  return base
})

/** Fixed aspect (logo square, banner, menu thumb): stencil must stay “cover”-able; wheel zooms image, not stencil size. */
const hasFixedAspect = computed(
  () => props.aspectRatio != null && props.aspectRatio > 0
)

/**
 * Largest axis-aligned crop with props.aspectRatio that fits inside the image (image pixel space).
 * Used for non–viewport-stencil modes only (free aspect).
 */
function maxCropSizeInImage(imageSize, aspectRatio) {
  const iw = imageSize.width
  const ih = imageSize.height
  let w = iw
  let h = w / aspectRatio
  if (h > ih) {
    h = ih
    w = h * aspectRatio
  }
  return { width: w, height: h }
}

function defaultCropSize({ imageSize }) {
  const ar = props.aspectRatio
  if (ar == null || ar <= 0) return {}
  return maxCropSizeInImage(imageSize, ar)
}

function defaultCropPosition({ coordinates, imageSize }) {
  if (!coordinates?.width || !coordinates?.height) {
    return { left: 0, top: 0 }
  }
  return {
    left: (imageSize.width - coordinates.width) / 2,
    top: (imageSize.height - coordinates.height) / 2,
  }
}

const cropperBehavior = computed(() => {
  if (!hasFixedAspect.value) {
    return {
      imageRestriction: 'fit-area',
      resizeImage: true,
    }
  }
  const ar = props.aspectRatio

  // Square (logo + menu item): letterboxed source + viewport-sized square stencil + auto zoom.
  if (isSquareLogoAspect()) {
    return {
      imageRestriction: 'stencil',
      autoZoom: true,
      stencilSize: stencilSizeSquareViewport,
      resizeImage: {
        touch: true,
        wheel: { ratio: 0.14 },
        adjustStencil: false,
      },
    }
  }

  // Banner ~21:9: same pattern so the frame is large and obvious.
  if (Math.abs(ar - 21 / 9) < 0.02) {
    return {
      imageRestriction: 'stencil',
      autoZoom: true,
      stencilSize: stencilSizeBannerViewport,
      resizeImage: {
        touch: true,
        wheel: { ratio: 0.14 },
        adjustStencil: false,
      },
    }
  }

  return {
    imageRestriction: 'stencil',
    resizeImage: {
      touch: true,
      wheel: { ratio: 0.14 },
      adjustStencil: false,
    },
    defaultSize: defaultCropSize,
    defaultPosition: defaultCropPosition,
  }
})

function onCropperReady() {
  void nextTick(() => {
    cropperRef.value?.refresh()
  })
}

function onClose() {
  if (!confirming.value) emit('cancel')
}

function onConfirm() {
  const cropper = cropperRef.value
  if (!cropper || confirming.value) return
  const result = cropper.getResult()
  if (!result || !result.canvas) {
    emit('cancel')
    return
  }
  confirming.value = true
  const asPng = props.exportFormat === 'png'
  const mime = asPng ? 'image/png' : 'image/jpeg'
  const ext = asPng ? '.png' : '.jpg'
  result.canvas.toBlob(
    (blob) => {
      if (!blob) {
        confirming.value = false
        emit('cancel')
        return
      }
      const base = props.fileName || (asPng ? 'image.png' : 'image.jpg')
      const name = base.replace(/\.[^.]+$/i, ext)
      const file = new File([blob], name, { type: mime, lastModified: Date.now() })
      emit('confirm', file)
      confirming.value = false
    },
    mime,
    asPng ? undefined : 0.92
  )
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) confirming.value = false
  }
)
</script>

<style scoped>
.cropper {
  --cropper-overlay-color: rgba(0, 0, 0, 0.4);
}
:deep(.vue-advanced-cropper) {
  min-height: 240px;
}
:deep(.vue-advanced-cropper__background) {
  background: #1a1a1a;
}
</style>
